import { UserPlusIcon } from '@heroicons/react/24/outline';
import { EllipsisVerticalIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import {
  Button,
  Card,
  CardBody,
  Chip,
  Divider,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Spinner,
} from '@heroui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import { DateTime } from 'luxon';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import AccountConfirmModal from '@/app/components/account/account-confirm-modal.tsx';
import ConnectRepDialog from '@/app/components/account/connect-rep-dialog.tsx';
import RepresentativeEditDialog from '@/app/components/reps/representative-edit-dialog.tsx';
import { formatDate } from '@/app/helpers/date.helpers.ts';
import { useRejectAccountRepresentative } from '@/app/hooks/use-reject-account-representative.ts';
import type { AccountRepresentativeListModel } from '@/app/types/account-representative.type.ts';
import { RepresentativeStatusEnum } from '@/app/types/representative-status.type.ts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useSort,
} from '@/primitives/table.tsx';
import { useFetchAccountRepresentativesForAccountQuery } from '@/slices/account-representative-api-slice.ts';

interface AccountConnectedRepsProps {
  accountKey: string;
  accountId: number;
  accountName: string;
}

const getRepName = (rep: AccountRepresentativeListModel) =>
  `${rep.representativeFirstName} ${rep.representativeLastName}`;

const getRepSortName = (rep: AccountRepresentativeListModel) =>
  `${rep.representativeLastName} ${rep.representativeFirstName}`.toLowerCase();

const getConnectedTimestamp = (rep: AccountRepresentativeListModel) =>
  rep.statusUpdatedAt ?? rep.requestSentAt ?? '';

const formatConnectedTimestamp = (rep: AccountRepresentativeListModel) => {
  const timestamp = getConnectedTimestamp(rep);
  if (!timestamp) {
    return '--';
  }

  return formatDate(timestamp, DateTime.DATETIME_SHORT, '--');
};

const RepStatusChip: FC<{ rep: AccountRepresentativeListModel }> = ({ rep }) => {
  const statusId = rep.representativeStatus?.id;

  if (statusId === RepresentativeStatusEnum.Approved) {
    return (
      <Chip size='sm' variant='flat' color='success'>
        Connected
      </Chip>
    );
  }

  if (statusId === RepresentativeStatusEnum.Rejected) {
    return (
      <Chip size='sm' variant='flat' color='danger'>
        Rejected
      </Chip>
    );
  }

  return (
    <Chip size='sm' variant='flat' color='warning'>
      Pending
    </Chip>
  );
};

const AccountConnectedReps: FC<AccountConnectedRepsProps> = ({
  accountKey,
  accountId,
  accountName,
}) => {
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [selectedRep, setSelectedRep] = useState<AccountRepresentativeListModel | null>(null);
  const { rejectConnection, isRejecting } = useRejectAccountRepresentative();
  const { data: reps, isLoading } = useFetchAccountRepresentativesForAccountQuery(
    accountKey ?? skipToken,
  );

  const sortingDataAccessor = useCallback((rep: AccountRepresentativeListModel, column: string) => {
    switch (column) {
      case 'name':
        return getRepSortName(rep);
      case 'status':
        return rep.representativeStatus?.id ?? RepresentativeStatusEnum.Pending;
      case 'connectedAt':
        return getConnectedTimestamp(rep);
      default:
        return '';
    }
  }, []);

  const { sort, setSort, sortData } = useSort({
    data: reps ?? [],
    memoizedSortingDataAccessor: sortingDataAccessor,
  });

  useEffect(() => {
    setSort({ column: 'connectedAt', direction: 'desc' });
  }, [setSort]);

  const connectedCount = useMemo(
    () =>
      (reps ?? []).filter(rep => rep.representativeStatus?.id === RepresentativeStatusEnum.Approved)
        .length,
    [reps],
  );
  const otherRequestCount = (reps?.length ?? 0) - connectedCount;
  const hasReps = Boolean(sortData.length);
  const otherRequestsLabel =
    otherRequestCount > 0
      ? ` · ${otherRequestCount} other request${otherRequestCount === 1 ? '' : 's'}`
      : '';
  const selectedRepName = selectedRep ? getRepName(selectedRep) : '';
  const canRemoveSelected =
    selectedRep?.representativeStatus?.id === RepresentativeStatusEnum.Approved;

  const openEdit = (rep: AccountRepresentativeListModel) => {
    setSelectedRep(rep);
    setEditDialogOpen(true);
  };

  const openRemove = (rep: AccountRepresentativeListModel) => {
    setSelectedRep(rep);
    setRemoveDialogOpen(true);
  };

  const handleRemove = async () => {
    if (!selectedRep) {
      return;
    }

    try {
      await rejectConnection(selectedRep);
      setRemoveDialogOpen(false);
      setSelectedRep(null);
    } catch {
      // toast handled in hook
    }
  };

  return (
    <>
      <Card shadow='sm' className='max-w-screen-lg self-center w-full'>
        <CardBody className='p-6'>
          <div className='flex flex-wrap items-center justify-between gap-3'>
            <div>
              <h2 className='text-lg font-semibold text-gray-900'>Connected Representatives</h2>
              <p className='text-sm text-gray-500 mt-1'>
                {connectedCount} connected{otherRequestsLabel}
              </p>
            </div>
            <Button
              size='sm'
              variant='bordered'
              color='secondary'
              startContent={<UserPlusIcon className='size-4' />}
              onPress={() => setConnectDialogOpen(true)}
            >
              Connect Rep
            </Button>
          </div>

          <Divider className='my-5' />

          {isLoading && (
            <div className='flex justify-center py-8'>
              <Spinner size='sm' color='primary' />
            </div>
          )}

          {!isLoading && !hasReps && (
            <p className='text-sm text-gray-500 py-4 text-center'>
              No representatives are connected to this account yet.
            </p>
          )}

          {!isLoading && hasReps && (
            <Table currentSort={sort} setCurrentSort={setSort} dense bleed={false}>
              <TableHead>
                <TableRow>
                  <TableHeader sortColumn='name'>Name</TableHeader>
                  <TableHeader sortColumn='status'>Status</TableHeader>
                  <TableHeader sortColumn='connectedAt'>Connected</TableHeader>
                  <TableHeader>Category</TableHeader>
                  <TableHeader className='text-right'>Actions</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortData.map(rep => {
                  const repName = getRepName(rep);
                  const categoryLabel = rep.accountCategory?.description;
                  const profileHref = `/rep-public-profile/${rep.representativeKey}`;
                  const isApproved =
                    rep.representativeStatus?.id === RepresentativeStatusEnum.Approved;

                  return (
                    <TableRow key={rep.key} href={profileHref} title={`View ${repName}`}>
                      <TableCell>
                        <div className='relative z-10'>
                          <p className='text-sm font-medium text-gray-900'>{repName}</p>
                          <p className='text-sm text-gray-600'>{rep.representativeEmailAddress}</p>
                          {rep.representativeCompany && (
                            <p className='text-xs text-gray-500'>{rep.representativeCompany}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className='relative z-10'>
                          <RepStatusChip rep={rep} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className='relative z-10 text-sm text-gray-700'>
                          {formatConnectedTimestamp(rep)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className='relative z-10'>
                          {categoryLabel ? (
                            <Chip size='sm' variant='flat' color='primary'>
                              {categoryLabel}
                            </Chip>
                          ) : (
                            <span className='text-sm text-gray-400'>--</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className='relative z-10 flex justify-end'>
                          <Dropdown placement='bottom-end'>
                            <DropdownTrigger>
                              <Button
                                isIconOnly
                                size='sm'
                                variant='flat'
                                color='default'
                                aria-label={`Actions for ${repName}`}
                              >
                                <EllipsisVerticalIcon className='size-5' />
                              </Button>
                            </DropdownTrigger>
                            <DropdownMenu>
                              <DropdownItem
                                key='edit'
                                startContent={<PencilIcon className='size-5' />}
                                onPress={() => openEdit(rep)}
                              >
                                Edit settings
                              </DropdownItem>
                              {isApproved && (
                                <DropdownItem
                                  key='remove'
                                  color='danger'
                                  className='text-danger'
                                  startContent={<TrashIcon className='size-5' />}
                                  onPress={() => openRemove(rep)}
                                >
                                  Remove connection
                                </DropdownItem>
                              )}
                            </DropdownMenu>
                          </Dropdown>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardBody>
      </Card>

      <ConnectRepDialog
        accountKey={accountKey}
        accountName={accountName}
        isOpen={connectDialogOpen}
        onClose={() => setConnectDialogOpen(false)}
      />
      <RepresentativeEditDialog
        isOpen={editDialogOpen}
        setIsOpen={setEditDialogOpen}
        accountRepKey={selectedRep?.key}
        accountId={selectedRep?.accountId ?? accountId}
      />
      <AccountConfirmModal
        isOpen={removeDialogOpen && canRemoveSelected}
        onClose={() => setRemoveDialogOpen(false)}
        onConfirm={handleRemove}
        isLoading={isRejecting}
        eyebrow='Connection'
        title='Remove connection'
        description={`Remove ${selectedRepName} from ${accountName}? They will not be able to request meetings here until they are connected again.`}
        confirmLabel='Remove connection'
        tone='danger'
        icon={<TrashIcon className='size-5' />}
      />
    </>
  );
};

export default AccountConnectedReps;
